// The explorer's "All files" list and navigation across changed files in it.
import type { Category } from './categorize';
import type { BlocksDone } from './filefilter';
import { buildTree, treeOrder, type TreeNode } from './tree';

export interface AllFilesFilter {
  /** Every known path: the repository tree at the head plus the PR's changed files. */
  paths: Iterable<string>;
  changed: Set<string>;
  /** Changed files that pass the changed-file filters (category, search, unreviewed, file filter). */
  changedShown: Set<string>;
  /** The file filter (type chips and path pattern) for unchanged files; none when omitted. */
  pass?: (path: string) => boolean;
  /** Lowercased search text ('' for none). */
  q: string;
  category: Category | 'all';
  categoryOf: (path: string) => Category;
  /**
   * Search match (DiffModel.matches): the path, or the source of changed
   * files and of files already opened. Other files' contents are not
   * fetched for search.
   */
  matches: (path: string, q: string) => boolean;
}

/** Paths the "All files" explorer shows. */
export function filterAll(f: AllFilesFilter): string[] {
  const out: string[] = [];
  for (const p of f.paths) {
    if (f.changed.has(p)) {
      if (f.changedShown.has(p)) out.push(p);
      continue;
    }
    if (f.pass && !f.pass(p)) continue;
    if (f.category !== 'all' && f.categoryOf(p) !== f.category) continue;
    if (f.q && !f.matches(p, f.q)) continue;
    out.push(p);
  }
  return out;
}

/**
 * Changed files after (`delta` > 0) or before the current file, nearest
 * first, in explorer order: what n/p and ]c/[c step through. `visible` is
 * the explorer's order, which may include unchanged files; `navOrder` the
 * changed files among them. From an unchanged file, navigation continues
 * with the changed files around its place in `visible`.
 */
export function changedAround(current: string | null, delta: number, navOrder: string[], visible: string[], changed: Set<string>): string[] {
  if (!current) return delta > 0 ? navOrder : [];
  const i = navOrder.indexOf(current);
  if (i >= 0) return delta > 0 ? navOrder.slice(i + 1) : navOrder.slice(0, i).reverse();
  const j = visible.indexOf(current);
  if (j < 0) return delta > 0 ? navOrder : [...navOrder].reverse();
  const seq = delta > 0 ? visible.slice(j + 1) : visible.slice(0, j).reverse();
  return seq.filter((p) => changed.has(p));
}

/**
 * The changed files n/p try after running off the end: from the first
 * changed file (delta > 0) or the last, up to and including the current
 * one, so a single changed file wraps to itself. For a current file that is
 * not a changed one, every changed file.
 */
export function wrapAround(current: string | null, delta: number, navOrder: string[]): string[] {
  const dir = delta > 0 ? navOrder : [...navOrder].reverse();
  const i = current ? dir.indexOf(current) : -1;
  return i < 0 ? dir : dir.slice(0, i + 1);
}

/**
 * Lines reviewed over the changed files the file filter keeps (all of
 * them when `pass` is omitted): the explorer total, header meter and rail.
 */
export function reviewProgress(
  paths: Iterable<string>,
  progress: (path: string) => { total: number; done: number },
  pass?: (path: string) => boolean,
): { total: number; done: number; filtered: boolean } {
  let total = 0;
  let done = 0;
  for (const p of paths) {
    if (pass && !pass(p)) continue;
    const s = progress(p);
    total += s.total;
    done += s.done;
  }
  return { total, done, filtered: !!pass };
}

/**
 * Change blocks reviewed over the changed files the file filter keeps,
 * as `reviewProgress` counts lines: from `reviewed`, each change block of
 * a file and whether it is reviewed (the reader's own count), null while
 * the file's diff is not computed (counted in `unknown`).
 */
export function blockProgress(paths: Iterable<string>, reviewed: (path: string) => readonly boolean[] | null, pass?: (path: string) => boolean): BlocksDone {
  let done = 0;
  let total = 0;
  let unknown = 0;
  for (const p of paths) {
    if (pass && !pass(p)) continue;
    const flags = reviewed(p);
    if (!flags) unknown++;
    else {
      total += flags.length;
      done += flags.filter(Boolean).length;
    }
  }
  return { done, total, unknown };
}

/**
 * Blocks as the summary shows them ("3 / 120 blocks"), beside its lines:
 * with files not counted yet the total is a floor ("3 / ≥120 blocks"),
 * and with none counted, still counting. `spoken` and `title` say why.
 */
export function blocksFigure(b: BlocksDone): { figure: string; spoken: string; title: string } {
  const files = (n: number) => `${count(n)} file${n === 1 ? '' : 's'}`;
  if (b.unknown && !b.total) return { figure: 'blocks: counting…', spoken: 'Change blocks: still counting.', title: `Change blocks are counted once each file's diff is computed; ${files(b.unknown)} to go` };
  const floor = b.unknown ? '≥' : '';
  return {
    figure: `${count(b.done)} / ${floor}${count(b.total)} blocks`,
    spoken: `${count(b.done)} of ${b.unknown ? 'at least ' : ''}${count(b.total)} change blocks reviewed${b.unknown ? `, ${files(b.unknown)} not counted yet` : ''}.`,
    title: `Change blocks, as the reader counts them (Change 2 / 20)${b.unknown ? `: ${files(b.unknown)} not counted until their diffs are computed` : ''}`,
  };
}

/**
 * Complexity reviewed and in all, over files' change blocks
 * (`thinkProgress`); files not counted yet (their diffs not computed);
 * and files whose diff is shown but that the engine did not score (shown
 * plainer, or the engine unavailable), which nothing will count; and the
 * files scored (a total of 0 may be scored files that ask nothing).
 */
export interface ThinkDone {
  done: number;
  total: number;
  unknown: number;
  unscored: number;
  scored: number;
}

/**
 * The summary's third figure: each change block's complexity score
 * (`think`, per file, null until its diff is computed, `unscored` for a
 * diff the engine did not score), summed, and the part in blocks
 * `reviewed` (the same files' flags, block for block).
 */
export function thinkProgress(
  paths: Iterable<string>,
  think: (path: string) => readonly number[] | 'unscored' | null,
  reviewed: (path: string) => readonly boolean[] | null,
  pass?: (path: string) => boolean,
): ThinkDone {
  let done = 0;
  let total = 0;
  let unknown = 0;
  let unscored = 0;
  let scored = 0;
  for (const p of paths) {
    if (pass && !pass(p)) continue;
    const scores = think(p);
    if (scores === 'unscored') {
      unscored++;
      continue;
    }
    if (!scores) {
      unknown++;
      continue;
    }
    scored++;
    const flags = reviewed(p);
    scores.forEach((s, i) => {
      total += s;
      if (flags?.[i]) done += s;
    });
  }
  return { done, total, unknown, unscored, scored };
}

/**
 * Think as the summary shows it ("think 12 / 340"), beside lines and
 * blocks: a floor while files are not counted yet or could not be scored
 * ("think 12 / ≥340"); with none scored, still counting while some are
 * being diffed, else not available. `spoken` and `title` say what it is.
 */
export function thinkFigure(t: ThinkDone): { figure: string; spoken: string; title: string } {
  const files = (n: number) => `${count(n)} file${n === 1 ? '' : 's'}`;
  const what = 'How much the changes ask of a reviewer: each change block\'s complexity, summed (its band\'s figure; moves not counted)';
  const couldNot = `Changeling couldn't work it out for ${files(t.unscored)}`;
  if (t.unknown && !t.scored) return { figure: 'think: counting…', spoken: 'Complexity: still counting.', title: `${what}; counted once each file's diff is computed, ${files(t.unknown)} to go` };
  if (t.unscored && !t.scored) return { figure: 'think: not available', spoken: `Complexity: not available. ${couldNot}.`, title: `${what}: not available: ${couldNot}` };
  const floor = t.unknown || t.unscored ? '≥' : '';
  const notScored = `${files(t.unscored)} Changeling couldn't score`;
  const spokenGaps = [t.unknown ? `${files(t.unknown)} not counted yet` : '', t.unscored ? notScored : ''].filter(Boolean);
  const titleGaps = [t.unknown ? `${files(t.unknown)} not counted until their diffs are computed` : '', t.unscored ? notScored : ''].filter(Boolean);
  return {
    figure: `think ${count(t.done)} / ${floor}${count(t.total)}`,
    spoken: `Complexity reviewed: ${count(t.done)} of ${floor ? 'at least ' : ''}${count(t.total)}${spokenGaps.map((g) => `, ${g}`).join('')}.`,
    title: `${what}${titleGaps.length ? `: ${titleGaps.join('; ')}` : ''}`,
  };
}

/** A count as it is shown: with thousands separators. */
export const count = (n: number): string => n.toLocaleString('en-US');

/** The review counts of the Files view, each over what it names. */
export interface ScopedProgress {
  /** The comparison shown: what the file list, each file's count and n / p go by. */
  shown: { done: number; total: number; filtered: boolean };
  /** Its name ("e9433f2 → head") when it is another comparison than the pull request's own (base → head); else null. */
  comparison: string | null;
  /**
   * Beside another comparison, the pull request's own count (base → head,
   * under the same file filter): `exact` false while some of its files are
   * still being counted, null before any is. Not read for base → head.
   */
  whole: { done: number; total: number; exact: boolean } | null;
  /** What base → head is the whole of (the subject's facets' `noun`): a pull request unless said. */
  noun?: string;
}

/**
 * The file list's review summary. Base → head is one figure, the whole
 * pull request's. Another comparison shows its own count first, under its
 * name, and the whole pull request's second, so neither is taken for the
 * other. A line is reviewed, in either, when a review mark covers it,
 * whichever view the mark was made in; the totals are the lines each
 * comparison changes. `spoken` says all of it in one sentence per count.
 */
export function progressSummary(p: ScopedProgress): { scope: string; figure: string; whole: string | null; spoken: string } {
  const { done, total, filtered } = p.shown;
  const figure = `${count(done)} / ${count(total)} lines${filtered ? '' : ' reviewed'}`;
  const said = `${count(done)} of ${count(total)} lines reviewed`;
  const Whole = `Whole ${p.noun ?? 'pull request'}`;
  if (p.comparison === null) {
    return { scope: `${Whole} · base → head`, figure, whole: null, spoken: `${Whole}, base to head: ${said}${filtered ? ', counting only the files the filter keeps' : ''}.` };
  }
  const w = p.whole;
  const whole = !w ? `${Whole}: counting…` : `${Whole}: ${count(w.done)} / ${count(w.total)}${w.exact ? '' : ' so far'}`;
  const wholeSaid = !w ? `${Whole}: still counting.` : `${Whole}: ${count(w.done)} of ${count(w.total)} lines reviewed${w.exact ? '' : ' so far, still counting'}.`;
  return {
    scope: `This comparison · ${p.comparison}`,
    figure,
    whole,
    spoken: `This comparison, ${p.comparison.replace(/\s*→\s*/g, ' to ')}: ${said}. ${wholeSaid}${filtered ? ' Both count only the files the filter keeps.' : ''}`,
  };
}

export interface ChangedFilter {
  category: Category | 'all';
  /** Lowercased search text ('' for none). */
  q: string;
  matches: (path: string, q: string) => boolean;
  /** "Unreviewed": hide files whose blocks are all reviewed (`reviewed`), except the open file. */
  unreviewedOnly: boolean;
  reviewed: (path: string) => boolean;
  current: string | null;
  /** The path pattern of the file filter. */
  path: (path: string) => boolean;
  /** The type chips of the file filter. */
  type: (path: string) => boolean;
}

/**
 * The changed files the explorer lists: every filter at once. `base`
 * leaves out only the type chips (what the chips count); `shown` applies
 * them too.
 */
export function filterChanged<E extends { path: string; category: Category }>(entries: E[], f: ChangedFilter): { base: E[]; shown: E[] } {
  const base = entries.filter((e) => {
    if (!f.path(e.path)) return false;
    if (f.category !== 'all' && e.category !== f.category) return false;
    if (f.q && !f.matches(e.path, f.q)) return false;
    if (f.unreviewedOnly && e.path !== f.current && f.reviewed(e.path)) return false;
    return true;
  });
  return { base, shown: base.filter((e) => f.type(e.path)) };
}

/**
 * The lines a folder's changed files add and remove, summed over the files
 * counted so far (`counts` answers null until a file's diff is computed);
 * `complete` is false while any file is still uncounted. Null when none is
 * counted yet.
 */
export function subtreeDiff(
  paths: Iterable<string>,
  counts: (path: string) => { added: number; removed: number } | null,
): { added: number; removed: number; complete: boolean } | null {
  let added = 0;
  let removed = 0;
  let counted = 0;
  let uncounted = 0;
  for (const p of paths) {
    const c = counts(p);
    if (!c) {
      uncounted++;
      continue;
    }
    added += c.added;
    removed += c.removed;
    counted++;
  }
  return counted ? { added, removed, complete: uncounted === 0 } : null;
}

/**
 * The order to load a comparison's files in before the Files view is
 * open: the file it will open first (`remembered`, when the comparison
 * changes it; else the first in the explorer's order), then the rest in
 * the explorer's order (`view`), so the reader's next files come next.
 */
export function preloadOrder(paths: string[], remembered: string | null, view: 'tree' | 'list'): string[] {
  const order = view === 'tree' ? treeOrder(buildTree(paths)) : paths;
  return remembered && paths.includes(remembered) ? [remembered, ...order.filter((p) => p !== remembered)] : order;
}

/** What a folder's row in the explorer shows of the files below it, at any depth. */
export interface FolderTally {
  /** Changed lines (`reviewProgress` over its changed files), and of those the reviewed ones. */
  total: number;
  done: number;
  /** Files, changed or not. */
  count: number;
  /** Changed files. */
  changed: number;
  /** `subtreeDiff` of its changed files other than a rebase's. */
  diff: { added: number; removed: number; complete: boolean } | null;
  /** Its changed files the comparison lists (what its Reviewed box marks), and of those the ones every line of which is reviewed, and the ones with any line reviewed. */
  marked: number;
  reviewed: number;
  started: number;
}

/** What `folderTallies` reads of a changed file. */
export interface TallyFiles {
  changed: (path: string) => boolean;
  /** Listed by the comparison (a file of the repository tree may be changed and not listed yet). */
  listed: (path: string) => boolean;
  /** Changed only by a rebase (`FileEntry.noise`): left out of the folder's lines added and removed. */
  noise: (path: string) => boolean;
  progress: (path: string) => { total: number; done: number; exact: boolean };
  counts: (path: string) => { added: number; removed: number } | null;
}

/**
 * Every folder's tally of `root` (by folder path, the root's at its own),
 * in one pass from the files up: each file is read once, however deep, so
 * a file's figures changing costs one pass over the tree, not one walk of
 * each folder's whole subtree. A folder whose tally is the same as in
 * `before` keeps that object, so a row reading it is not re-run.
 */
export function folderTallies(root: TreeNode, files: TallyFiles, before?: ReadonlyMap<string, FolderTally>): Map<string, FolderTally> {
  const out = new Map<string, FolderTally>();
  const visit = (node: TreeNode) => {
    const t = { total: 0, done: 0, count: 0, changed: 0, marked: 0, reviewed: 0, started: 0 };
    let added = 0;
    let removed = 0;
    let counted = 0;
    let uncounted = 0;
    for (const f of node.folders) {
      const [c, d] = visit(f);
      t.total += c.total;
      t.done += c.done;
      t.count += c.count;
      t.changed += c.changed;
      t.marked += c.marked;
      t.reviewed += c.reviewed;
      t.started += c.started;
      added += d.added;
      removed += d.removed;
      counted += d.counted;
      uncounted += d.uncounted;
    }
    for (const p of node.files) {
      t.count++;
      if (!files.changed(p)) continue;
      t.changed++;
      const s = files.progress(p);
      t.total += s.total;
      t.done += s.done;
      if (files.listed(p)) {
        t.marked++;
        if (s.exact && s.done === s.total) t.reviewed++;
        if (s.done > 0) t.started++;
      }
      if (files.noise(p)) continue;
      const c = files.counts(p);
      if (!c) uncounted++;
      else {
        added += c.added;
        removed += c.removed;
        counted++;
      }
    }
    const diff = counted ? { added, removed, complete: uncounted === 0 } : null;
    const was = before?.get(node.path);
    const same =
      was &&
      (Object.keys(t) as (keyof typeof t)[]).every((k) => was[k] === t[k]) &&
      (was.diff === diff || (!!was.diff && !!diff && was.diff.added === diff.added && was.diff.removed === diff.removed && was.diff.complete === diff.complete));
    out.set(node.path, same ? was : { ...t, diff });
    return [t, { added, removed, counted, uncounted }] as const;
  };
  visit(root);
  return out;
}

/** What decides the file the Files view opens on a comparison. */
export interface Opening {
  /** The file the URL names (`?file=`). */
  requested: string | null;
  /** The file already open. */
  current: string | null;
  /** The file last open on this pull request. */
  remembered: string | null;
  /** The comparison's changed files. */
  changed: Set<string>;
  /** True for a file the view can open (changed, or in the repository tree). */
  openable: (path: string) => boolean;
  /** The first changed file in the explorer's order, null when none. */
  first: string | null;
}

/**
 * The file to open: the one the URL names, else the one already open,
 * when they can be opened; else the remembered file when this comparison
 * changes it; else the first changed file. A link to a commit or a range
 * that leaves the remembered file alone thus opens on a change.
 */
export function openingFile(o: Opening): string | null {
  if (o.requested && o.openable(o.requested)) return o.requested;
  if (o.current && o.openable(o.current)) return o.current;
  if (o.remembered && o.changed.has(o.remembered)) return o.remembered;
  return o.first;
}
